import {useEffect, useMemo, useRef, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import { Box, IconButton, Tooltip } from "@mui/material"
import { FilterAlt } from "@mui/icons-material";

import {withTranslation} from "react-i18next";
import {useDispatch} from "react-redux";

import targetInvSimulationResultService from "@plannel/services/ip/target-inv-simulation-result-service";
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import PaginationContainer from "@plannel/components/PaginationContainer"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";
import CustomHeaderGroup from "@plannel/components/aggrid/CustomHeaderGroup";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";

import FilterContainer from "@plannel/components/layout/FilterContainer";
import { isEmpty, isEqual, size, replace, cloneDeep, isNull, isUndefined } from "lodash";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { IPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import IpVersionFilter from "@plannel/components/filter/IpVersionFilter";
import CodeUtil from "@plannel/pages/utils/code-util";
import NumberUtil from "@plannel/utils/number-util";

import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import { IPFilterModal } from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/FilterModal";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import { FilterButton } from "@plannel/components/ActionIconButton";

const SALES_QTY = "salesQty";
const SALES_AMT = "salesAmt";
const SALES_QTY_AVG = "salesQtyAvg";
const SALES_QTY_STD = "salesQtyStd";
const SHIPMENT_QTY = "shipmentQty";
const SHIPMENT_AMT = "shipmentAmt";
const SHIPMENT_QTY_AVG = "shipmentAvg";
const SHIPMENT_QTY_STD = "shipmentStd";
const { snackBarMessageColor } = ColorUtil;
const { versionStatusCode, muicolor, filterType, item, dpABCConfig, classifiBaseCode, classifiDimLCode } = CodeUtil;
const CLASSIFI_DIM_OPTIONS = Object.values(classifiDimLCode);
const CLASSIFI_BASE_OPTIONS = [
  classifiBaseCode.QUANTITY,
  classifiBaseCode.REVENUE,
  classifiBaseCode.AMOUNT,
  classifiBaseCode.PROFIT,
  classifiBaseCode.COGS
];
const { PLANNED, READY, RELEASED } = versionStatusCode;
const TargetInventorySimulationResults = ({t, viewName, title}) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ? reduxViewState.currentPage : 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [ipVersionFilter, setIpVersionFilter] = useState(reduxViewState?.ipVersion ? reduxViewState.ipVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });
  const [itemCustomValue, setItemCustomValue] = useState(reduxViewState?.filter?.category ?? []);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [pendingAdvancedFilters, setPendingAdvancedFilters] = useState(null);
  const [plantLocAnalysis, setPlantLocAnalysis] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? muicolor.INFO : muicolor.DEFAULT);
  const [isAFActive, setIsAFActive] = useState(!isEmpty(reduxViewState?.advancedFilters));
  const [openFilter, setOpenFilter] = useState(false);

  const [totalPages, setTotalPages] = useState(0);
  const [simulationResults, setSimulationResults] = useState([]);
  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencyMap = useRef({});
  const [csvColDef, setCsvColDef] = useState([]);
  const EXCEL_EXPORT = "excelExport";
  const EFFECTIVE_SERVICE_LEVEL = "effectiveSlPct"
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getRequestParams = (isPagination = true) => {
    const params = {};

    if (ipVersionFilter) {
      params.versionId = ipVersionFilter.id;
    }

    if (!isAFActive) {
      if (filterValue?.category) {
        params.categoryCds = filterValue?.category?.filter((category) => category?.leafFlg)?.map((category) => category?.hrchyCd);
      }

      if (filterValue?.locations) {
        params.locCds = filterValue?.locations?.map(location => location?.locCd);
      }

      if (!isEmpty(itemFilterList)) {
        params.itemFilterList = itemFilterList;
        params.itemFilterType = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
      }

      if (!isEmpty(invClassCdList)) {
        params.invClassCdList = invClassCdList;
      }
    } else if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return { ...params, ...sortParams };
  };

    const currencyRenderer = (params, isExport = false) => {
    const locCd = params?.data?.locCd;
    let symbol = userData?.currencySymbol;
    if (size(Object.keys(currencyMap?.current))) {
      const currencyCd = currencyMap.current[locCd];
      if (currencyCd) {
        symbol = CodeUtil.getCurrencySymbol(currencyCd);
      } 
    }
    
    const value = params?.data?.[params?.colDef?.field];
    if (isNull(value) || isUndefined(value)) {
      return "";
    }
    const formatValue = NumberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (isExport) {
      if (params?.colDef?.field === EFFECTIVE_SERVICE_LEVEL) {
        return NumberUtil.formatNumber(value, { maximumFractionDigits: 4, minimumFractionDigits: 4 });
      }
      return formatValue.replace(/,/g, "");
    }
    if (value < 0) {
      return `-${symbol}${replace(formatValue, "-", "")}`;
    }
    return symbol + formatValue;
  };
  currencyRenderer.customType = EXCEL_EXPORT;

  const valueFormatter = (params) => {
    return GridUtils.gridValueL10N(params.value);
  }


  const columnDefs = [
    { headerName: "location", field: "locName", width: 120, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "itemCd", field: "itemCd", cellClass: 'stringType', width: 120, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "itemName", field: "itemName", width: 200, type: ["nonEditableColumn"], pinned: "left", lockPinned: true },
    { headerName: "categoryCd", field: "categoryCd", width: 120, type: ["nonEditableColumn"], hide: true },
    { headerName: "categoryName", field: "categoryName", width: 150, type: ["nonEditableColumn"] },
    {
      headerName: "classEval", groupId: "classification",
      headerGroupComponent: CustomHeaderGroup,
      children: [
        { headerName: "invClassCd", field: "invClassCd", width: 150, type: ["nonEditableColumn"], cellRenderer: AbcXyzChip },
        {
          headerName: "classifiDimCd",
          columnGroupShow: "open",
          field: "classifiDimCd",
          valueFormatter,
          minWidth: 100,
          width: 150,
          type: ["nonEditableColumn"]
        },
        {
          headerName: "evalStartDt",
          columnGroupShow: "open",
          field: "evalStartDt",
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"),
          minWidth: 70,
          width: 115,
          type: ["nonEditableColumn"]
        },
        {
          headerName: "evalEndDt",
          columnGroupShow: "open",
          field: "evalEndDt",
          valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"),
          minWidth: 70,
          width: 115,
          type: ["nonEditableColumn"]
        },
        { headerName: "base", columnGroupShow: "open", field: "classifiBaseCd", valueFormatter, minWidth: 60, width: 100, type: ["nonEditableColumn"] }
      ]
    },
    {
      headerName: "targetStock", groupId: "targetStock",
      headerGroupComponent: CustomHeaderGroup,
      children: [
        {
          headerName: "targetInvQty",
          field: "targetInvQty",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "cycleInvQty",
          field: "cycleInvQty",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        // Safety Stock
        {
          headerName: "safetyStockQty",
          field: "safetyStockQty",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        // Target Stock Amt
        {
          headerName: "targetInvAmt",
          columnGroupShow: "open",
          field: "targetInvAmt",
          minWidth: 80,
          width: 120,
          valueFormatter: currencyRenderer,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        // Safety Stock Amt
        {
          headerName: "safetyStockAmt",
          columnGroupShow: "open",
          field: "safetyStockAmt",
          minWidth: 80,
          width: 120,
          valueFormatter: currencyRenderer,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        // Target Stock Days
        {
          headerName: "targetInvDays",
          columnGroupShow: "open",
          field: "targetInvDays",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        // Safety Stock Days
        {
          headerName: "safetyStockDays",
          columnGroupShow: "open",
          field: "safetyStockDays",
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        { headerName: "invPolicy", columnGroupShow: "open", field: "invPolicyDesc", width: 150, minWidth: 110, type: ["nonEditableColumn"] },
        {
          headerName: "otdLt",
          columnGroupShow: "open",
          field: "avgLtDays",
          minWidth: 50,
          width: 90,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: SALES_QTY,
          columnGroupShow: "open",
          field: SALES_QTY,
          minWidth: 60,
          width: 100,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: SALES_AMT,
          columnGroupShow: "open",
          field: SALES_AMT,
          minWidth: 60,
          width: 100,
          valueFormatter: currencyRenderer,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: SALES_QTY_AVG,
          columnGroupShow: "open",
          field: SALES_QTY_AVG,
          minWidth: 80,
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: SALES_QTY_STD,
          columnGroupShow: "open",
          field: SALES_QTY_STD,
          minWidth: 100,
          width: 140,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        {
          headerName: "orderFreq",
          columnGroupShow: "open",
          field: "rpFreq",
          minWidth: 100,
          width: 140,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        }
      ]
    },
    {
      headerName: "onhandStock", groupId: "targetStock",
      children: [
        { headerName: "onhandQty", field: "currentInvQty", width: 100, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        { headerName: "onhandAmt", field: "currentInvAmt", width: 100, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer },
        { headerName: "lastMInvDays", field: "invDays", width: 100, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        { headerName: "effectiveSl", field: "effectiveSlPct", width: 140, type: ["percentColumn", "nonEditableColumn", "rightAligned"] },
        {
          headerName: "excessQty",
          field: "invGapQty",
          width: 120,
          type: ["numberColumn", "nonEditableColumn", "rightAligned"]
        },
        { headerName: "excessDays", field: "excessDays", width: 120, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        { headerName: "avgStockMonthly", field: "avgInvQty", width: 140, type: ["numberColumn", "nonEditableColumn", "rightAligned"] },
        { headerName: "avgStockMonthlyAmt", field: "avgInvAmt", width: 140, type: ["numberColumn", "nonEditableColumn", "rightAligned"], valueFormatter: currencyRenderer }
      ]
    }
  ];

  const advancedFilterColumnDefs = [
    { headerName: "location", filterName: "locName", filterType: "string" },
    { headerName: "locCd", filterName: "locCd", filterType: "string" },
    { headerName: "itemName", filterName: "itemName", filterType: "string" },
    { headerName: "itemCd", filterName: "itemCd", filterType: "string" },
    { headerName: "categoryName", filterName: "categoryName", filterType: "string" },
    { headerName: "categoryCd", filterName: "categoryCd", filterType: "string" },
    {
      headerName: "classEval", 
      children: [
        { headerName: "invClassCd", filterName: "invClassCd", filterType: "string", filterOption: dpABCConfig },
        { headerName: "classifiDimCd",  filterName: "classifiDimCd", filterType: "string", filterOption: CLASSIFI_DIM_OPTIONS },
        { headerName: "evalStartDt", filterName: "evalStartDt", filterType: "date", filterUnit: "month" },
        { headerName: "evalEndDt",  filterName: "evalEndDt", filterType: "date", filterUnit: "month" },
        { headerName: "base", filterName: "classifiBaseCd", filterType: "string", filterOption: CLASSIFI_BASE_OPTIONS }
      ]
    },
    {
      headerName: "targetStock",
      children: [
        { headerName: "targetInvQty", filterName: "targetInvQty", filterType: "number" },
        { headerName: "cycleInvQty", filterName: "cycleInvQty", filterType: "number" },
        { headerName: "safetyStockQty", filterName: "safetyStockQty", filterType: "number" },
        { headerName: "targetInvAmt", filterName: "targetInvAmt", filterType: "number" },
        { headerName: "safetyStockAmt", filterName: "safetyStockAmt", filterType: "number" },
        { headerName: "targetInvDays", filterName: "targetInvDays", filterType: "number" },
        { headerName: "safetyStockDays", filterName: "safetyStockDays", filterType: "number" },
        { headerName: "invPolicy", filterName: "invPolicyDesc", filterType: "string" },
        { headerName: "otdLt", filterName: "avgLtDays", filterType: "number" },
        { headerName: plantLocAnalysis ? SHIPMENT_QTY : SALES_QTY, filterName: SALES_QTY, filterType: "number" },
        { headerName: plantLocAnalysis ? SHIPMENT_AMT : SALES_AMT, filterName: SALES_AMT, filterType: "number" },
        { headerName: plantLocAnalysis ? SHIPMENT_QTY_AVG : SALES_QTY_AVG, filterName: SALES_QTY_AVG, filterType: "number" },
        { headerName: plantLocAnalysis ? SHIPMENT_QTY_STD : SALES_QTY_STD, filterName: SALES_QTY_STD, filterType: "number" },
        { headerName: "orderFreq", filterName: "rpFreq", filterType: "number" }
      ]
    },
    {
      headerName: "onhandStock",
      children: [
        { headerName: "onhandQty", filterName: "currentInvQty", filterType: "number"},
        { headerName: "onhandAmt", filterName: "currentInvAmt", filterType: "number" },
        { headerName: "lastMInvDays", filterName: "invDays", filterType: "number" },
        { headerName: "effectiveSl", filterName: "effectiveSlPct", filterType: "percent" },
        { headerName: "excessQty", filterName: "invGapQty", filterType: "number" },
        { headerName: "excessDays", filterName: "excessDays", filterType: "number" },
        { headerName: "avgStockMonthly", filterName: "avgInvQty", filterType: "number" },
        { headerName: "avgStockMonthlyAmt", filterName: "avgInvAmt", filterType: "number" }
      ]
    }
  ];

  const ITEM_SEARCH_TO_AF = {
    itemCd: "itemCd",
    name: "itemName",
    itemHrchy: "categoryName",
    invClassCd: "invClassCd",
  };

  const buildInitialFromCurrentFilters = () => {
    const nodes = [];
    const locCds = filterValue?.locations ? filterValue.locations.map(location => location?.locCd).filter(Boolean) : [];
    const categoryCds = filterValue?.category ? filterValue.category.map(category => category?.hrchyCd).filter(Boolean) : [];

    if (locCds.length > 1) {
      nodes.push({ logicOperator: "OR", children: locCds.map(locCd => ({ filterType: "string", headerName: "locCd", operator: "equals", values: [locCd] })) });
    } else if (locCds.length === 1) {
      nodes.push({ filterType: "string", headerName: "locCd", operator: "equals", values: [locCds[0]] });
    }

    if (categoryCds.length > 1) {
      nodes.push({ logicOperator: "OR", children: categoryCds.map(categoryCd => ({ filterType: "string", headerName: "categoryCd", operator: "equals", values: [categoryCd] })) });
    } else if (categoryCds.length === 1) {
      nodes.push({ filterType: "string", headerName: "categoryCd", operator: "equals", values: [categoryCds[0]] });
    }

    const [itemLogic, itemCompare] = itemFilterType ?? [filterType.or.cd, filterType.equal.cd];
    const itemOperator = itemCompare === filterType.notEqual.cd ? "notEqual" : "equals";
    const itemLogicOperator = itemLogic === filterType.and.cd ? "AND" : "OR";
    const unsupportedItemKeys = [];
    const itemNodes = [];

    for (const [key, values] of Object.entries(itemFilterList ?? {})) {
      const targets = (values ?? []).filter(Boolean);
      if (targets.length === 0) {
        continue;
      }
      const headerName = ITEM_SEARCH_TO_AF[key];
      if (!headerName) {
        unsupportedItemKeys.push(key);
        continue;
      }
      const leaf = value => ({ filterType: "string", headerName, operator: itemOperator, values: [value] });
      if (targets.length > 1) {
        itemNodes.push({ logicOperator: itemLogicOperator, children: targets.map(leaf) });
      } else {
        itemNodes.push(leaf(targets[0]));
      }
    }

    if (itemNodes.length > 1) {
      nodes.push({ logicOperator: itemLogicOperator, children: itemNodes });
    } else if (itemNodes.length === 1) {
      nodes.push(itemNodes[0]);
    }

    if (invClassCdList.length > 1) {
      nodes.push({ logicOperator: "OR", children: invClassCdList.map(cd => ({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [cd] })) });
    } else if (invClassCdList.length === 1) {
      nodes.push({ filterType: "string", headerName: "invClassCd", operator: "equals", values: [invClassCdList[0]] });
    }

    const filterTree = nodes.length > 0 ? { logicOperator: "AND", children: [{ logicOperator: "AND", children: nodes }] } : { logicOperator: "AND", children: [] };

    return { filterTree, unsupportedItemKeys };
  };

  const dynamicHeaderName = (plantLocAnalysis) => {
    const newColumns = cloneDeep(columnDefs);

    if (plantLocAnalysis) {
      newColumns.forEach((column) => {
        const targetHeaderName = "targetStock";

        if (column?.headerName === targetHeaderName) {
          const childrenColumns = column?.children;

          childrenColumns.forEach((childrenColumn) => {
            const field = childrenColumn?.field;

            if (field === SALES_QTY) {
              childrenColumn.headerName = SHIPMENT_QTY;
            } else if (field === SALES_AMT) {
              childrenColumn.headerName = SHIPMENT_AMT;
            } else if (field === SALES_QTY_AVG) {
              childrenColumn.headerName = SHIPMENT_QTY_AVG;
            } else if (field === SALES_QTY_STD) {
              childrenColumn.headerName = SHIPMENT_QTY_STD;
            }
          });
        }

      });
    }

    GridUtils.setColumnDefs({ ...gridRef?.current, columnDefs: newColumns, viewName });
    setCsvColDef(newColumns);
  }

  const retrieveSimulationResults = () => {
    const params = getRequestParams();

    targetInvSimulationResultService.getAll(params)
      .then(response => {
        const { data } = response;

        setPlantLocAnalysis(data?.plantLocAnalysis === true);

        if (data.results && data.results.length > 0) {
          dynamicHeaderName(data?.plantLocAnalysis);
          setSimulationResults(data.results);
        } else {
          dynamicHeaderName(data?.plantLocAnalysis);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
          setSimulationResults([]);
        }

        if (size(data?.currency)) {
          currencyMap.current = data?.currency;
        }

        setTotalPages(data.totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const pageState = reduxUtil.createObjectState(viewName, { currentPage, pageSize });
        const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
        const reduxState = {...pageState, ...filterState};
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
  };

  const onGridReady = (params) => {
    setGridLoading(true);
    setCsvColDef(cloneDeep(columnDefs));
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveSimulationResults();
    }
  }

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const handleFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handleFilterOpen = () => {
    if (!openFilter) {
      if (!isAFActive) {
        const { filterTree, unsupportedItemKeys } = buildInitialFromCurrentFilters();
        setPendingAdvancedFilters(filterTree);

        if (unsupportedItemKeys.length > 0) {
          const fields = unsupportedItemKeys.map(key => t(key)).join(", ");
          setSnackInfo({ open: true, severity: snackBarMessageColor.warning, content: t("MSG_UnsupportedItemFilterInAdvancedFilter", { fields }) });
        }
      } else {
        setPendingAdvancedFilters(null);
      }
    }
    setOpenFilter(prev => !prev);
  };

  const resetAdvancedFilter = () => {
    if (isAFActive) {
      setIsAFActive(false);
    }
    setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
    setAdvancedFilters(null);
    setOpenFilter(false);
  };

  const handleAdvancedFilterApply = (filters) => {
    const normalized = filters?.children?.length > 0 ? filters : null;

    if (!normalized) {
      setPendingAdvancedFilters({ logicOperator: "AND", children: [] });
      setAdvancedFilters(null);
      setIsAFActive(false);
      return;
    }

    const clearedFilterValue = { locations: null, category: null };

    setFilterValue(clearedFilterValue);
    setItemCustomValue([]);
    setItemFilterList({});
    setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    setInvClassCdList([]);
    setAdvancedFilters(normalized);
    setPendingAdvancedFilters(normalized);
    setIsAFActive(true);
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createFilterViewState(viewName, clearedFilterValue)));
  };


  const normalizeFilterValue = (value) => ({
    ...value,
    locations: value?.locations?.length > 0 ? value.locations : null,
    category: value?.category?.length > 0 ? value.category : null,
  });

  const handleFilterValueChange = (newValue) => {
    const normalized = normalizeFilterValue(newValue);

    if (isAFActive && (normalized.locations || normalized.category)) {
      resetAdvancedFilter();
    }
    if (normalized.category && !isEmpty(itemFilterList)) {
      setItemFilterList({});
      setItemFilterType([filterType.or.cd, filterType.equal.cd]);
    }
    setFilterValue(prev => isEqual(prev, normalized) ? prev : normalized);
  };

  const handleInvClassCdListChange = (newList) => {
    if (isAFActive && newList?.length > 0) {
      resetAdvancedFilter();
    }
    setInvClassCdList(prev => isEqual(prev, newList) ? prev : newList);
  };

  useEffect(() => {
    if (gridLoading && !isEmpty(ipVersionFilter)) {
      retrieveSimulationResults();
    }

    return () => {
      setSimulationResults([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (gridLoading && !isEmpty(ipVersionFilter)) {
      initPageToRetrieve();
    }

    return () => {
      setSimulationResults([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipVersionFilter?.id, filterValue, itemFilterList, itemFilterType, invClassCdList, advancedFilters]);

  useEffect(() => {
    if (filterData.filterList) {
      const newFilterList = filterData.filterList ?? {};

      if (isAFActive && !isEmpty(newFilterList)) {
        resetAdvancedFilter();
      }
      if (!isEmpty(newFilterList)) {
        setItemCustomValue([]);
        setFilterValue(prev => prev?.category ? { ...prev, category: null } : prev);
      }
      setItemFilterList(prev => isEqual(prev, newFilterList) ? prev : newFilterList);
      setItemFilterType(prev => isEqual(prev, filterData.filterType) ? prev : filterData.filterType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
      if (itemFilterList && Object.keys(itemFilterList)?.length > 0) {
        setItemFilterColor(muicolor.INFO);
      } else {
        setItemFilterColor(muicolor.DEFAULT);
      }
      if (reduxViewState) {
        reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, itemFilterList, itemFilterType, invClassCdList, advancedFilters }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemFilterList, itemFilterType, invClassCdList, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      setAdvancedFilterColor(muicolor.INFO);
    } else {
      setAdvancedFilterColor(muicolor.DEFAULT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <IpVersionFilter viewName={viewName} ipVersionValue={ipVersionFilter} onChangeFilter={setIpVersionFilter} statusFilters={[READY, PLANNED, RELEASED]} />
        <IPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={handleFilterValueChange} addLocType={[CodeUtil.locationType.PLANT.cd]}/>
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={handleFilterValueChange} customValue={itemCustomValue} enableMultiSelect selectLeafs/>
        <IconButton
          aria-describedby={"prodHrchyPop"}
          size="small"
          sx={{ mr: 1 }}
          onClick={filterActionItem}
          color={itemFilterColor}>
          <Tooltip title={t("itemFilter")}>
            <FilterAlt />
          </Tooltip>
        </IconButton>
        <AbcXyzFilterModal invClassCdList={invClassCdList} setInvClassCdList={handleInvClassCdListChange} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={targetInvSimulationResultService}  params={getRequestParams(false)} columnDefs={csvColDef.filter(column => column.hide !== true)}/>
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={handleFilterOpen} />
        </Box>
      </FilterContainer>      <Box id="target-inventory-simulation-result" className="ag-theme-balham grid-area" sx={{ mb: 1, display: "flex", flexDirection: "row" }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <AgGridReact
            ref={gridRef}
            rowData={simulationResults}
            {...defaultGridMemo}
            onGridReady={onGridReady}
            onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            statusBar={statusBar}
          />
        </Box>
        {openFilter && (
          <ResizableFilterPanel>
            <AdvancedFilter
              columnDefs={advancedFilterColumnDefs}
              advancedFilters={pendingAdvancedFilters ?? advancedFilters}
              onApply={handleAdvancedFilterApply}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={totalPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <IPFilterModal
        open={filterModalOpen}
        close={handleFilterClose}
        displayModButtons
        modelCode={item.name}
        modalResultData={setFilterData}
        filterTypes={{ Item: itemFilterType ?? [filterType.or.cd, filterType.equal.cd] }}
        filteredList={{ Item: itemFilterList ?? {} }}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(TargetInventorySimulationResults);